import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { MainCategory, Category } from '@flows.cash/types'
import { useDefaultCategories } from '../hooks/useDefaultCategories'
import { storage } from '../utils/storage'
import { ensureValidIcon } from '../utils/categoryIcons'
import { Platform } from '../utils/platform'
import { supabase } from '../services/supabase'
import { v4 as uuidv4 } from 'uuid'
import { 
  migrateCategoriesToUuid as migrateCategoriesToUuidUtil,
  isValidUuid
} from '../utils/categoryMigration'

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

// Helper function to convert default categories to use UUIDs
const convertToUuidCategories = (categories: MainCategory[]): MainCategory[] => {
  return categories.map(category => {
    // Generate a UUID for the main category
    const mainCategoryId = uuidv4();
    
    // Map subcategories with new UUIDs
    const subcategories = category.subcategories.map(subcategory => ({
      ...subcategory,
      id: uuidv4(),
      parentId: mainCategoryId
    }));
    
    return {
      ...category,
      id: mainCategoryId,
      subcategories
    };
  });
};

// Helper function to process categories from Supabase
const processCategories = (categories: any[], categoryCountMap?: Map<string, number>): MainCategory[] => {
  // Group categories by parent_id
  const categoryMap: Record<string, any> = {};
  const mainCategories: MainCategory[] = [];

  // First, identify all categories
  categories.forEach(category => {
    // Ensure the icon is valid
    category.icon = ensureValidIcon(category);
    
    // Store in map for easy lookup
    categoryMap[category.id] = category;
  });

  // Process categories into main categories and subcategories
  categories.forEach(category => {
    if (!category.parent_id) {
      // This is a main category - calculate total transaction count for all its subcategories
      let totalTransactionCount = 0;
      if (categoryCountMap) {
        // Count transactions for this main category itself (if it has transactions directly)
        totalTransactionCount += categoryCountMap.get(category.id) || 0;
        
        // Count transactions for all subcategories under this main category
        categories.forEach(subcat => {
          if (subcat.parent_id === category.id) {
            totalTransactionCount += categoryCountMap.get(subcat.id) || 0;
          }
        });
      }
      
      mainCategories.push({
        id: category.id,
        name: category.name,
        type: category.type,
        color: category.color,
        icon: category.icon,
        isExpanded: false,
        description: category.description,
        trackInNetWorth: category.track_in_net_worth,
        isRecurring: category.is_recurring,
        subcategories: []
      });
    }
  });

  // Add subcategories to their parent categories
  categories.forEach(category => {
    if (category.parent_id) {
      // Find the parent category
      const mainCategory = mainCategories.find(mc => mc.id === category.parent_id);
      if (mainCategory) {
        const transactionCount = categoryCountMap ? (categoryCountMap.get(category.id) || 0) : 0;
        mainCategory.subcategories.push({
          id: category.id,
          name: category.name,
          type: category.type,
          color: category.color,
          icon: category.icon,
          parentId: category.parent_id,
          transactionCount: transactionCount,
          description: category.description,
          trackInNetWorth: category.track_in_net_worth,
          isRecurring: category.is_recurring
        });
      }
    }
  });

  return mainCategories;
};

interface CategoriesStore {
  categories: MainCategory[]
  loading: boolean
  error: string | null
  initialized: boolean
  fetchCategories: (userId?: string) => Promise<void>
  saveCategories: (userId: string, categories: MainCategory[]) => Promise<void>
  updateTransactionCounts: (userId: string, transactions: Array<{ categoryId?: string }>) => Promise<void>
  defaultCategories: MainCategory[]
  clearCategories: () => void
  migrateCategoriesToUuid: (userId: string) => Promise<Record<string, string> | undefined>
}

const defaultCategories = useDefaultCategories()

export const useCategoriesStore = create<CategoriesStore>()(
  persist(
    (set, get) => ({
      categories: [],
      loading: false,
      error: null,
      initialized: false,
      defaultCategories,

      fetchCategories: async (userId?: string) => {
        if (!userId) return;
        
        set({ loading: true, error: null });
        
        try {
          // Fetch all categories for the user (both main and sub)
          const { data: categories, error } = await supabase
            .from('categories')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: true });
            
          if (error) throw error;
          
          if (!categories || categories.length === 0) {
            // If no categories exist, create default categories
            const defaultCategoriesWithUuids = convertToUuidCategories(defaultCategories);
            await get().saveCategories(userId, defaultCategoriesWithUuids);
            set({ 
              categories: defaultCategoriesWithUuids,
              loading: false,
              initialized: true 
            });
            return;
          }

          // Fetch transaction counts for categories dynamically
          const { data: transactionCounts, error: countError } = await supabase
            .from('transactions')
            .select('category_id')
            .eq('user_id', userId)
            .not('category_id', 'is', null);

          if (countError) {
            console.warn('Error fetching transaction counts:', countError);
          }

          // Count transactions per category
          const categoryCountMap = new Map<string, number>();
          if (transactionCounts) {
            transactionCounts.forEach(transaction => {
              if (transaction.category_id) {
                const count = categoryCountMap.get(transaction.category_id) || 0;
                categoryCountMap.set(transaction.category_id, count + 1);
              }
            });
          }

          // Process the categories into the correct hierarchical structure with dynamic counts
          const processedCategories = processCategories(categories, categoryCountMap);
          
          set({ 
            categories: processedCategories,
            loading: false,
            initialized: true 
          });
        } catch (error) {
          console.error('Error fetching categories:', error);
          set({ 
            error: (error as Error).message,
            loading: false 
          });
        }
      },

      saveCategories: async (userId: string, categories: MainCategory[]) => {
        if (!userId) return;
        
        try {
          // Check if user is still authenticated
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.user) {
            console.log('User not authenticated, skipping saveCategories to Supabase');
            // Just update local state without saving to Supabase
            set({ categories, error: null });
            return;
          }
          
          // Delete existing categories for this user
          const { error: deleteError } = await supabase
            .from('categories')
            .delete()
            .eq('user_id', userId);
            
          if (deleteError) throw deleteError;
          
          // Prepare categories for saving
          const categoriesToSave: any[] = [];
          
          // Process main categories and their subcategories
          categories.forEach(category => {
            // Add main category
            categoriesToSave.push({
              id: isValidUuid(category.id) ? category.id : uuidv4(),
              user_id: userId,
              name: category.name,
              type: category.type,
              color: category.color,
              icon: category.icon,
              parent_id: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
            
            // Add subcategories
            category.subcategories.forEach(subcategory => {
              categoriesToSave.push({
                id: isValidUuid(subcategory.id) ? subcategory.id : uuidv4(),
                user_id: userId,
                name: subcategory.name,
                type: subcategory.type,
                color: subcategory.color,
                icon: subcategory.icon,
                parent_id: category.id,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              });
            });
          });
          
          // Insert all categories
          const { error: insertError } = await supabase
            .from('categories')
            .upsert(categoriesToSave);
            
          if (insertError) throw insertError;
          
          // Update local state
          set({ categories, error: null });
        } catch (error) {
          console.error('Error saving categories:', error);
          set({ error: (error as Error).message });
        }
      },

      updateTransactionCounts: async (userId: string, transactions: Array<{ categoryId?: string }>) => {
        if (!userId) {
          console.error('No userId provided for updateTransactionCounts');
          return;
        }

        const currentCategories = [...get().categories];

        // Reset all counts
        currentCategories.forEach((category: MainCategory) => {
          category.subcategories.forEach((subcat: Category) => {
            subcat.transactionCount = 0;
          });
        });

        // Count transactions for each category and subcategory
        transactions.forEach((transaction) => {
          if (!transaction.categoryId) return;

          // Handle different category ID formats (for backward compatibility)
          let mainCatId, subCatId;
          
          // Check if it's a UUID format (most likely from Supabase)
          const isUuidFormat = isValidUuid(transaction.categoryId);
          
          if (isUuidFormat) {
            // If it's a UUID, use it directly as the subcategory ID
            subCatId = transaction.categoryId;
          } else if (transaction.categoryId.includes(':')) {
            // Legacy format with colon separator (from Firebase)
            [mainCatId, subCatId] = transaction.categoryId.split(':');
          } else if (transaction.categoryId.includes('/')) {
            // Legacy format with slash separator
            [mainCatId, subCatId] = transaction.categoryId.split('/');
          } else {
            // If no separator found, the whole ID might be the subcategory ID
            subCatId = transaction.categoryId;
          }

          if (!subCatId) return;

          // First try to find by subcategory ID directly
          let found = false;
          for (const mainCategory of currentCategories) {
            const subCategory = mainCategory.subcategories.find((sub: Category) => sub.id === subCatId);
            if (subCategory) {
              subCategory.transactionCount = (subCategory.transactionCount || 0) + 1;
              found = true;
              break;
            }
          }

          // If not found and we have mainCatId, try with main category
          if (!found && mainCatId) {
            const mainCategory = currentCategories.find((cat: MainCategory) => cat.id === mainCatId);
            if (mainCategory) {
              const subCategory = mainCategory.subcategories.find((sub: Category) => sub.id === subCatId);
              if (subCategory) {
                subCategory.transactionCount = (subCategory.transactionCount || 0) + 1;
              }
            }
          }
        });

        // Update the categories in Supabase
        try {
          const { error } = await supabase
            .from('categories')
            .upsert(
              currentCategories.map((category: MainCategory) => ({
                ...category,
                user_id: userId,
                updated_at: new Date().toISOString()
              }))
            );

          if (error) {
            console.error('Error updating category counts:', error);
            return;
          }

          // Update local state
          set({ categories: currentCategories });
        } catch (error) {
          console.error('Error updating category counts:', error);
        }
      },

      migrateCategoriesToUuid: async (userId: string): Promise<Record<string, string> | undefined> => {
        if (!userId) {
          console.error('No userId provided for migrateCategoriesToUuid');
          return undefined;
        }

        set({ loading: true, error: null });

        try {
          // Use the utility function to migrate categories
          await migrateCategoriesToUuidUtil(userId);
          
          // Fetch the updated categories
          await get().fetchCategories(userId);
          
          set({ loading: false });
          // Since the migration utility doesn't return an ID mapping, we return undefined
          // indicating the migration was successful but no mapping is available
          return undefined;
        } catch (error) {
          console.error('Error migrating categories to UUIDs:', error);
          set({ error: (error as Error).message, loading: false });
          return undefined;
        }
      },

      clearCategories: () => {
        set({ categories: [], initialized: false });
      }
    }),
    {
      name: 'categories-storage',
      storage: createJSONStorage(() => getStorage())
    }
  )
);
